// © Microsoft Corporation. All rights reserved.

import { BrowserVersionOperator, IStringTargetScopeType, PageRequestTargetScope } from "@msnews/config-data-types";

/**
 * TargetScopeMatcher
 * This is for comparing targetscope from the config with targetscope from the page.
 */

/**
 * basic version of target scope that only checks market currently.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope matches with config target scope, otherwise false
 */
export function targetScopeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    // if target scope is undefined for a config entry, we treat that as default config and apply it
    if (!configTargetScope) {
        return true;
    }

    return (audienceModeMatched(configTargetScope, pageTargetScope)
        && browserTypeMatched(configTargetScope, pageTargetScope)
        && deviceTypeMatched(configTargetScope, pageTargetScope)
        && domainMatched(configTargetScope, pageTargetScope)
        && experimentMatched(configTargetScope, pageTargetScope)
        && marketMatched(configTargetScope, pageTargetScope)
        && ocidMatched(configTargetScope, pageTargetScope)
        && osMatched(configTargetScope, pageTargetScope)
        && pageTypeMatched(configTargetScope, pageTargetScope)
        && platformMatched(configTargetScope, pageTargetScope));
}

/**
 * function to check if current audienceMode type matches target audienceMode type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope audienceMode matches with config target scope audienceMode, otherwise false
 */
function audienceModeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.audienceMode || pageTargetScope.audienceMode === configTargetScope.audienceMode;
}

/**
 * function to check if current browser type matches target browser type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope browser type matches with config target scope browser type, otherwise false
 */
function browserTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    if (!configTargetScope.browser
        || !configTargetScope.browser.version
        || !configTargetScope.browser.browserType) {
        return true;
    }

    // Proceed if browserType exists and it matches
    if (pageTargetScope.browser
        && pageTargetScope.browser.browserType
        && pageTargetScope.browser.browserType === configTargetScope.browser.browserType) {

        const pcsBrowserVersion = pageTargetScope.browser.version;
        if (!pcsBrowserVersion) {
            return true;
        }

        const configBrowserVersion = configTargetScope.browser.version;

        // If browser version in config index scope is "87", match with "87" from browser version in page target scope
        if (typeof configBrowserVersion === "string"
            && pcsBrowserVersion === configBrowserVersion) {
            return true;
        }

        // If browser version is object, check if matches.
        if (typeof configBrowserVersion === "object") {
            const pcsIntBrowserVersion = parseInt(pcsBrowserVersion as string);
            for (const operator in configBrowserVersion) {
                const value = configBrowserVersion[operator];

                // value is of type number so it can be 0. Hence check for undefined or null explicitly
                if (value !== undefined && value !== null) {
                    switch (operator) {
                        case BrowserVersionOperator.Equals:
                            if (pcsIntBrowserVersion !== value) { return false; }
                            break;

                        case BrowserVersionOperator.GreaterThan:
                            if (!(pcsIntBrowserVersion > value)) { return false; }
                            break;

                        case BrowserVersionOperator.LessThan:
                            if (!(pcsIntBrowserVersion < value)) { return false; }
                            break;

                        case BrowserVersionOperator.NotEquals:
                            if (pcsIntBrowserVersion === value) { return false; }
                            break;

                        case BrowserVersionOperator.OneOf:
                            if ((value as number[]).indexOf(pcsIntBrowserVersion) === -1) { return false; }
                            break;

                        default: return false;
                    }
                }
            }

            return true;
        }
    }

    return false;
}

/**
 * function to check if current device type matches target device type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope device type matches with config target scope device type, otherwise false
 */
function deviceTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.deviceFormFactor || pageTargetScope.deviceFormFactor === configTargetScope.deviceFormFactor;
}

/**
 * Check if current domain matches target domain
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return Whether the current target scope domain matches with config target scope domain
 */
function domainMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.domain || pageTargetScope.domain === configTargetScope.domain;
}

/**
 * function to check if current experiment id matches with the requested scope experiment id.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope experiment matches with config target scope experiment, otherwise false
 */
function experimentMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    pageTargetScope.pageExperiments = pageTargetScope.pageExperiments || [];

    if (typeof configTargetScope.experimentId != "string" || Object.keys(configTargetScope.experimentId).length === 0
        || pageTargetScope.pageExperiments.indexOf(configTargetScope.experimentId) >= 0) {
        return true;
    }

    return false;
}

/**
 * Compare the strings case insensitive.
 * @private
 * @param {string} lhs The string to compare.
 * @param {string} rhs The string to compare against.
 * @return {number} - true if the strings equal case insensitive.
 */
function stringsEqualCaseInsensitive(lhs: string, rhs: string): boolean {
    return (lhs || "").localeCompare(rhs || "", undefined, { sensitivity: "accent" }) === 0;
}

/**
 * function to check if current market scope match with target scope market.
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope market matches with config target scope market, otherwise false
 */
function marketMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    // if locale is not part of target scope, we assume it is applicable for all locale.
    // Separated out this if condition to have clarity about this behavior.
    if (!configTargetScope.locale) {
        return true;
    }

    const targetLocale = configTargetScope.locale;
    const currentLocale = pageTargetScope.locale;

    if ((!targetLocale.language || stringsEqualCaseInsensitive(targetLocale.language, currentLocale.language)) &&
        (!targetLocale.market || stringsEqualCaseInsensitive(targetLocale.market, currentLocale.market)) &&
        (!targetLocale.script || stringsEqualCaseInsensitive(targetLocale.script, currentLocale.script))) {
        return true;
    }

    return false;
}

/**
 * function to check if current ocid matches target scope ocid
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope ocid matches with config target scope ocid, otherwise false
 */
function ocidMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return stringTargetScopeTypeMatched(configTargetScope.ocid, pageTargetScope.ocid as string);
}

/**
 * function to check if current operating system type matches target operating system type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope operating system type matches with config target scope operating system type, otherwise false
 */
function osMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.os || pageTargetScope.os === configTargetScope.os;
}

/**
 * function to check if current pagetype matches target scope
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope pagetype matches with config target scope pagetype, otherwise false
 */
function pageTypeMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    // If there is no pageType in config scope, meaning it should match.
    if (!configTargetScope.pageType) {
        return true;
    }

    // If there is pageType in config scope but no pageType in pageTargetScope, it's not matching
    if (!pageTargetScope.pageType) {
        return false;
    }

    const pcsPageType = pageTargetScope.pageType as string;
    const configPageType = configTargetScope.pageType;

    // Check if page type in config and pcs matches
    if (typeof configPageType === "string"
        && configPageType === pcsPageType) {
        return true;
    }

    if (!configPageType
        || typeof configPageType !== "object") {
        return false;
    }

    // If pageType is exact
    // Example: exact: "dhp"
    if (configPageType.exact
        && configPageType.exact.toUpperCase() === pcsPageType.toUpperCase()) {
        return true;
    }

    // Example:
    // config scope segments: { “0”: “news”, “1”: “politics” }
    // Does not match for above config scope when pcs pagetype: "news"
    // Does not match for above config scope when pcs pagetype: "news::coronavirus"
    // Match for above config scope when pcs pagetype: "news::politics"
    // Match for above config scope when pcs pagetype: "news::politics::election"
    if (configPageType.segments) {
        const segments = configPageType.segments;
        const pcsPageTypeSegments = pcsPageType.split("::");
        if (pcsPageTypeSegments && pcsPageTypeSegments.length > 0) {
            let index = 0;
            for (const key in segments) {
                const value = segments[key];
                if (!value
                    || typeof value !== "string"
                    // If a segment match is needed for an index where we don't have a segment, then we return false.
                    || index >= pcsPageTypeSegments.length
                    // See if the segment values match, if they don't then we return false.
                    || value.toUpperCase() !== pcsPageTypeSegments[index].toUpperCase()) {
                    return false;
                }

                index++;
            }

            // Make sure that config scope pagetype segments are traversed and return true.
            return Object.keys(segments).length === index;
        }
    }

    return false;
}

/**
 * function to check if current scope platform type matches target scope platform type
 * @private
 * @param {PageRequestTargetScope} configTargetScope - experience config reference that has targetscope as child property
 * @param {PageRequestTargetScope} pageTargetScope - page target scope, this is the targetscope from the page HTML
 * @return boolean - true if current target scope platform matches with config target scope platform, otherwise false
 */
function platformMatched(configTargetScope: PageRequestTargetScope, pageTargetScope: PageRequestTargetScope): boolean {
    return !configTargetScope.platform || pageTargetScope.platform === configTargetScope.platform;
}

/**
 * function to check if string target scope matches the page value.
 * @param configStringValue - string or IStringTargetScopeType value from the config.
 * @param pageStringValue - page target string value, cannot be an object.
 * @return boolean - true if current target scope platform matches with config target scope platform, otherwise false
 */
export function stringTargetScopeTypeMatched(configStringValue: string | IStringTargetScopeType, pageStringValue: string): boolean {
    if (!configStringValue) {
        return true;
    }

    if (!pageStringValue) {
        return false;
    }

    // Check if string typed ocid in config and PCS matches
    if (typeof configStringValue === "string" && stringsEqualCaseInsensitive(configStringValue, pageStringValue)) {
        return true;
    }

    // if its not an object, then return false.
    if (typeof configStringValue !== "object") {
        return false;
    }

    const lowerCasePageStringValue = pageStringValue.toLowerCase();
    if (configStringValue.startsWith && !lowerCasePageStringValue.startsWith(configStringValue.startsWith.toLowerCase())) {
        return false;
    }

    if (configStringValue.endsWith && !lowerCasePageStringValue.endsWith(configStringValue.endsWith.toLowerCase())) {
        return false;
    }

    if (configStringValue.equals && !stringsEqualCaseInsensitive(configStringValue.equals, pageStringValue)) {
        return false;
    }

    if (configStringValue.notEquals && stringsEqualCaseInsensitive(configStringValue.notEquals, pageStringValue)) {
        return false;
    }

    if (configStringValue.contains && !lowerCasePageStringValue.includes(configStringValue.contains.toLowerCase())) {
        return false;
    }

    if (configStringValue.oneOf && configStringValue.oneOf.indexOf(pageStringValue) === -1) {
        return false;
    }

    return true;
}